import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldAlert, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * LogRepair — structured repair entry (Item 7).
 */
export default function LogRepair() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    vehicle_id: "",
    title: "",
    odometer: "",
    date: new Date().toISOString().slice(0, 16),
    workshop: "",
    labour_amount: "",
    parts_amount: "",
    total_amount: "",
    warranty_months: "",
    notes: "",
  });

  useEffect(() => {
    api.get("/vehicles").then((r) => {
      const assigned = user?.assigned_vehicle_ids || [];
      const all = r.data || [];
      const list = (user?.is_admin || assigned.length === 0)
        ? all
        : all.filter((v) => assigned.includes(v.id));
      setVehicles(list);
    });
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) return toast.error("Select a vehicle");
    if (!form.title.trim()) return toast.error("Enter a repair title");
    if (!form.odometer) return toast.error("Enter the odometer reading");
    setSaving(true);
    try {
      await api.post("/service-logs", {
        vehicle_id: form.vehicle_id,
        kind: "repair",
        title: form.title.trim(),
        odometer: parseFloat(form.odometer),
        date: form.date,
        workshop: form.workshop || null,
        labour_amount: form.labour_amount ? parseFloat(form.labour_amount) : null,
        parts_amount:  form.parts_amount  ? parseFloat(form.parts_amount)  : null,
        total_amount:  form.total_amount  ? parseFloat(form.total_amount)  : null,
        warranty_months: form.warranty_months ? parseInt(form.warranty_months, 10) : null,
        notes: form.notes || null,
      });
      toast.success("Repair logged");
      nav("/", { replace: true });
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-2xl mx-auto space-y-6" data-testid="log-repair-page">
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => nav(-1)} className="w-9 h-9 border border-[var(--border-line)] hover:bg-[var(--hover)] flex items-center justify-center rounded-lg" aria-label="Back">
          <ArrowLeft size={18} />
        </button>
        <div>
          <div className="ft-overline text-xs">REPAIR</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter flex items-center gap-2">
            <ShieldAlert size={26} className="text-[#EF4444]" /> Log a repair
          </h1>
        </div>
      </div>

      <div className="rounded-xl border border-[var(--border-line)] bg-[var(--bg-surface)] p-5 space-y-4">
        <Field label="What was repaired?" required>
          <input type="text" required className="input" value={form.title}
            placeholder="e.g. Brake caliper replacement" data-testid="log-repair-title"
            onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </Field>
        <Field label="Vehicle" required>
          <select className="input" value={form.vehicle_id} required
            onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} data-testid="log-repair-vehicle">
            <option value="">Select vehicle</option>
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.number} · {v.name}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Odometer (km)" required>
            <input type="number" required className="input" value={form.odometer}
              onChange={(e) => setForm({ ...form, odometer: e.target.value })} data-testid="log-repair-odometer" />
          </Field>
          <Field label="Date">
            <input type="datetime-local" className="input" value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="log-repair-date" />
          </Field>
        </div>
        <Field label="Workshop / Garage">
          <input type="text" className="input" value={form.workshop}
            onChange={(e) => setForm({ ...form, workshop: e.target.value })} data-testid="log-repair-workshop" />
        </Field>
      </div>

      <div className="rounded-xl border border-[var(--border-line)] bg-[var(--bg-surface)] p-5 space-y-3">
        <div className="ft-overline">CHARGES</div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Labour ₹">
            <input type="number" step="0.01" className="input" value={form.labour_amount}
              onChange={(e) => setForm({ ...form, labour_amount: e.target.value })} />
          </Field>
          <Field label="Parts ₹">
            <input type="number" step="0.01" className="input" value={form.parts_amount}
              onChange={(e) => setForm({ ...form, parts_amount: e.target.value })} />
          </Field>
          <Field label="Total ₹">
            <input type="number" step="0.01" className="input" value={form.total_amount}
              onChange={(e) => setForm({ ...form, total_amount: e.target.value })} data-testid="log-repair-total" />
          </Field>
        </div>
        <Field label="Warranty on parts / labour (months)">
          <input type="number" step="1" className="input" value={form.warranty_months}
            onChange={(e) => setForm({ ...form, warranty_months: e.target.value })} />
        </Field>
        <Field label="Notes">
          <textarea className="input min-h-[70px]" value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>
      </div>

      <button type="submit" disabled={saving}
        className="w-full bg-[#FF5A1F] hover:bg-[#E64A0F] text-white font-bold py-3 rounded-lg inline-flex items-center justify-center gap-2"
        data-testid="log-repair-submit">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save repair
      </button>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="ft-overline block mb-1">{label}{required && <span className="text-[#FF5A1F]"> *</span>}</span>
      {children}
    </label>
  );
}
